import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, History, CalendarClock } from "lucide-react";
import api from "../lib/api";

/**
 * ServiceHistory — Iter 290o
 *
 * Standalone page (moved out of /equipment Service-history tab).
 * Lists every service-log row across all assets, filterable by
 * customer/site/free-text. Read-only audit log.
 *
 * Route: /service-history  ·  Pillar: Ops
 *
 * Data sources:
 *   GET /service-logs   →  service log rows
 *   GET /customers      →  for name lookup + filter dropdown
 *   GET /sites          →  for name lookup + filter dropdown
 */
export default function ServiceHistory() {
  const [logs, setLogs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterSite, setFilterSite] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/service-logs").catch(() => ({ data: [] })),
      api.get("/customers"),
      api.get("/sites"),
    ]).then(([l, c, s]) => {
      if (!alive) return;
      setLogs(l.data || []);
      setCustomers(c.data || []);
      setSites(s.data || []);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return logs
      .filter((l) => {
        if (filterCustomer && l.customer_id !== filterCustomer) return false;
        if (filterSite && l.site_id !== filterSite) return false;
        if (s) {
          if (
            !(l.equipment_name || "").toLowerCase().includes(s) &&
            !customerName(l.customer_id).toLowerCase().includes(s) &&
            !siteName(l.site_id).toLowerCase().includes(s) &&
            !(l.notes || "").toLowerCase().includes(s)
          ) return false;
        }
        return true;
      })
      .sort(
        (a, b) =>
          new Date(b.service_date || b.created_at) -
          new Date(a.service_date || a.created_at),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, q, filterCustomer, filterSite, customers, sites]);

  const customerSites = sites.filter(
    (s) => !filterCustomer || s.customer_id === filterCustomer,
  );

  const actionStyle = (action) => {
    if (action === "replaced") return { border: "#F59E0B66", color: "#F59E0B", bg: "#F59E0B1a" };
    if (action === "installed") return { border: "#3B82F666", color: "#3B82F6", bg: "#3B82F61a" };
    return { border: "#10B98166", color: "#10B981", bg: "#10B9811a" };
  };

  return (
    <div className="space-y-5" data-testid="service-history-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">
            Ops · Audit log
          </div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <History className="w-7 h-7 text-emerald-300" strokeWidth={1.6} />
            Service history
          </h1>
          <p className="text-xs text-[#647087] mt-1">
            Every service / installation / replacement logged against site assets.
          </p>
        </div>
        <Link
          to="/scheduler"
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="service-history-to-scheduler"
        >
          <CalendarClock className="w-3.5 h-3.5" /> Upcoming services
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#647087]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search asset, customer, site, notes…"
            className="hv-input hv-input-search"
            data-testid="service-history-search"
          />
        </div>
        <select
          value={filterCustomer}
          onChange={(e) => {
            setFilterCustomer(e.target.value);
            setFilterSite("");
          }}
          className="hv-input py-2 text-xs w-48"
          data-testid="service-history-customer-filter"
        >
          <option value="">All customers</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={filterSite}
          onChange={(e) => setFilterSite(e.target.value)}
          className="hv-input py-2 text-xs w-48"
          data-testid="service-history-site-filter"
        >
          <option value="">All sites</option>
          {customerSites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <span className="text-[10px] uppercase tracking-wider text-[#647087] ml-auto">
          {filtered.length} of {logs.length} log{logs.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      <div className="hv-card overflow-x-auto" data-testid="service-history-table">
        <table className="w-full text-sm" style={{ minWidth: 760 }}>
          <thead className="bg-[#0a0b0e] border-b border-white/10">
            <tr className="text-left text-[#647087] text-xs uppercase tracking-wider">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Asset</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Qty</th>
              <th className="px-4 py-3">Site</th>
              <th className="px-4 py-3">Notes</th>
              <th className="px-4 py-3">By</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => {
              const st = actionStyle(l.action);
              return (
                <tr
                  key={l.id}
                  className="border-b border-white/5 hover:bg-white/[0.03] transition"
                  data-testid={`service-history-row-${l.id}`}
                >
                  <td className="px-4 py-3 text-[#dde3ec] whitespace-nowrap text-xs">
                    {new Date(l.service_date || l.created_at).toLocaleDateString("en-GB")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{l.equipment_name || "—"}</div>
                    <div className="text-xs text-[#647087]">{customerName(l.customer_id)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold"
                      style={{ borderColor: st.border, color: st.color, background: st.bg }}
                    >
                      {l.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {l.quantity ? `${l.quantity} ${l.unit || "pcs"}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-[#a0aabf] text-xs">{siteName(l.site_id)}</td>
                  <td className="px-4 py-3 text-[#a0aabf] text-xs max-w-md">
                    <div className="line-clamp-2 whitespace-pre-wrap">{l.notes || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-[#dde3ec] text-xs">{l.by_user_name || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && (
          <div className="p-8 text-center text-[#647087] text-sm" data-testid="service-history-empty">
            {logs.length === 0
              ? "No service history yet. Log a service from a site's Equipment tab."
              : "No history matches these filters."}
          </div>
        )}
        {loading && (
          <div className="p-8 text-center text-[#647087] text-sm">Loading service history…</div>
        )}
      </div>
    </div>
  );
}
